import { onMessage } from 'firebase/messaging';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Status from '~/constants/Status';
import getMessaging from '~/libs/fcm/browser/getMessaging';
import messageListener, { ListenerPosition } from '~/libs/fcm/messageListener';
import useFCMAllow from '~/libs/hooks/useFCMAllow';
import useStoredFCMData from '~/libs/hooks/useStoredFCMData';
import { RootReducerState } from '~/modules';
import { manageFCMAction } from '~/modules/ManageFCMModule';

interface ReducerStates {
    manageFCMStatus: RootReducerState['manageFCMReducer']['status'];
    allowFCMStatus: RootReducerState['allowFCMReducer']['status'];
}

const useFCMService = () => {
    const dispatch = useDispatch();

    const [allow] = useFCMAllow();

    const { manageFCMStatus, allowFCMStatus } = useSelector<
        RootReducerState,
        ReducerStates
    >(({ manageFCMReducer, allowFCMReducer }) => ({
        manageFCMStatus: manageFCMReducer.status,
        allowFCMStatus: allowFCMReducer.status
    }));

    // localstorage 에 저장된 fcm data 조회
    const storedFCMData = useStoredFCMData();
    const {
        deviceID,
        locale,
        appVersion,
        osVersion,
        token,
        enabled,
        userInfo
    } = storedFCMData;

    // 최초 진입 시 token data 세팅
    useEffect(() => {
        if (
            allowFCMStatus !== Status.SUCCESS ||
            manageFCMStatus !== Status.INITIAL
        )
            return;

        dispatch(manageFCMAction.initialize(storedFCMData));
    }, [allow, allowFCMStatus, dispatch, manageFCMStatus, storedFCMData]);

    // 이후 값 업데이트 시 fcm data 세팅
    useEffect(() => {
        if (
            allowFCMStatus !== Status.SUCCESS ||
            manageFCMStatus !== Status.SUCCESS
        )
            return;

        dispatch(manageFCMAction.updateFCMData());
    }, [
        dispatch,
        token,
        deviceID,
        locale,
        osVersion,
        appVersion,
        allow,
        userInfo,
        allowFCMStatus,
        manageFCMStatus
    ]);

    // 알림 허용 여부 변경 시 데이터 변경
    useEffect(() => {
        if (
            allowFCMStatus !== Status.SUCCESS ||
            manageFCMStatus !== Status.SUCCESS
        )
            return;

        dispatch(manageFCMAction.updateEnabled(enabled));
    }, [allow, allowFCMStatus, dispatch, enabled, manageFCMStatus]);

    // 메세지 리스너 등록
    useEffect(() => {
        if (!allow) return;

        const messaging = getMessaging();
        onMessage(messaging, messageListener(ListenerPosition.FRONT));
    }, [allow]);
};

export default useFCMService;
